import { useEffect, useState } from 'react';
import { AMPLITUDE_EVENT_TYPES } from 'constants/amplitude';
import { toAnalyticsCurrency, toMajorUnits } from 'utils/walletUtil';
import { DEFAULT_PAGE_SIZE } from 'constants/tables';
import useAmplitude from 'utils/hooks/useAmplitude';
import searchUtil from 'utils/searchUtil';
import {
  useGetWireTransfers,
  useGetPendingFundingRequestsCount,
  useDownloadWireTransferProof,
} from 'services/reactQueryHooks/wireTransfers';
import {
  useApproveFundingRequest,
  useDeclineFundingRequest,
} from 'services/reactQueryHooks/fundingRequests';
import {
  statusForTab,
  createOrderString,
  mapWireTransferToFundingRequest,
  filterRequestsForTab,
  getRequestCompanyToLog,
} from './helper';

const useFundingRequestsPage = () => {
  const { sendAmplitudeEvent } = useAmplitude();
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [orderBy, setOrderBy] = useState(null);
  const [downloadingProof, setDownloadingProof] = useState(null); // { wireTransferId, fileName, company }
  const [approveError, setApproveError] = useState(null);

  const { mutate: approveFundingRequest, isLoading: isApproving } = useApproveFundingRequest();
  const { mutate: declineFundingRequest, isLoading: isDeclining } = useDeclineFundingRequest();

  // isFetching (not isLoading) so the table's spinner also shows on tab/page/sort
  // changes, not just the very first request.
  const { data, isFetching } = useGetWireTransfers({
    pageNumber: currentPage,
    pageSize,
    status: statusForTab(activeTab),
    orderBy,
    filter: searchValue || undefined,
  });

  // The Pending tab's count badge must show regardless of which tab is active.
  // Also over-fetches and filters client-side (see the hook) since the API has no
  // way to filter by proof status, so this is the only accurate pending count.
  const { pendingCount } = useGetPendingFundingRequestsCount();

  const {
    isFetching: isDownloadingProof,
    isSuccess: isDownloadProofSuccess,
    isError: isDownloadProofError,
  } = useDownloadWireTransferProof({
    wireTransferId: downloadingProof?.wireTransferId,
    fileName: downloadingProof?.fileName,
    company: downloadingProof?.company,
  });

  const requests = filterRequestsForTab(
    (data?.items || []).map(mapWireTransferToFundingRequest),
    activeTab,
  );
  // On the Pending tab, the server's totalCount doesn't account for the proof-status
  // filter above, so use the same accurate, client-filtered count for consistency.
  const totalCount = activeTab === 'pending' ? pendingCount : data?.totalCount || 0;

  const handleReview = request => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.fundingRequestReview, {
      company: getRequestCompanyToLog(request),
      requestId: request?.id,
      amount: toMajorUnits(request?.amountMinor),
      currency: toAnalyticsCurrency(request?.currency),
    });
    setApproveError(null);
    setReviewingRequest(request);
  };

  // Clearing the error here too means the banner can never outlive the modal session
  // that produced it — reopening a request always starts from a clean state.
  const handleCloseReview = () => {
    setApproveError(null);
    setReviewingRequest(null);
  };

  // The proof can be downloaded from the table row or from the review modal
  const findRequest = id =>
    reviewingRequest?.id === id ? reviewingRequest : requests.find(request => request.id === id);

  const handleDownloadProof = ({ wireTransferId, fileName }) => {
    setDownloadingProof({
      wireTransferId,
      fileName,
      company: getRequestCompanyToLog(findRequest(wireTransferId)),
    });
  };

  // Clears the in-flight id once the download settles, so the same file can be
  // downloaded again and the button/link re-enables.
  useEffect(() => {
    if (
      downloadingProof &&
      !isDownloadingProof &&
      (isDownloadProofSuccess || isDownloadProofError)
    ) {
      setDownloadingProof(null);
    }
  }, [downloadingProof, isDownloadingProof, isDownloadProofSuccess, isDownloadProofError]);

  // The request under review, for the decision events' company/amount/currency
  const getDecisionAnalytics = id =>
    reviewingRequest?.id === id
      ? {
          company: getRequestCompanyToLog(reviewingRequest),
          amountMinor: reviewingRequest.amountMinor,
          currency: reviewingRequest.currency,
        }
      : {};

  const handleApprove = (id, { depositReference, notes }) => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.fundingRequestApprove);
    setApproveError(null);
    approveFundingRequest(
      { id, depositReference, notes, ...getDecisionAnalytics(id) },
      {
        onSuccess: handleCloseReview,
        // The modal shows a translated generic message, never this raw string
        // (httpWrapper already logs the real error to Datadog on every failure) —
        // this only needs to be truthy to trigger the error banner and drop the
        // reviewer back to the form from the "Approve and credit" confirm step.
        onError: error => setApproveError(error?.errors?.message || true),
      },
    );
  };

  const handleDecline = (id, { depositReference, notes }) => {
    // A failed approve leaves its banner up; declining is a fresh decision, so the
    // stale approve error must not sit on screen while the decline is in flight.
    setApproveError(null);
    declineFundingRequest(
      { id, depositReference, notes, ...getDecisionAnalytics(id) },
      { onSuccess: handleCloseReview },
    );
  };

  const handleTabChange = tabKey => {
    setActiveTab(tabKey);
    setCurrentPage(1);
  };

  const handleSearch = value => {
    setSearchValue(searchUtil.trimValue(value));
    setCurrentPage(1);
  };

  const handlePageChange = pageNumber => setCurrentPage(pageNumber);

  const handlePageSizeChange = (_current, newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    setOrderBy(createOrderString(sorter.field, sorter.order));
  };

  useEffect(() => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.fundingRequestsPage);
  }, [sendAmplitudeEvent]);

  return {
    searchValue,
    activeTab,
    reviewingRequest,
    requests,
    totalCount,
    pendingCount,
    isLoading: isFetching,
    currentPage,
    pageSize,
    isApproving,
    isDeclining,
    approveError,
    downloadingProofId: downloadingProof?.wireTransferId || null,
    onSearch: handleSearch,
    onTabChange: handleTabChange,
    onReview: handleReview,
    onCloseReview: handleCloseReview,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onTableChange: handleTableChange,
    onApprove: handleApprove,
    onDecline: handleDecline,
    onDownloadProof: handleDownloadProof,
  };
};

export default useFundingRequestsPage;
